import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { get } from 'lodash-es';

import { Button } from 'ui-components/components/button';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { AllocationsHeader } from './header';
import { InventoryAllocation } from './inventory-allocation';
import { DeallocationWarningModal } from './deallocation-warning-modal';
import AsModalWithContext from '../../../../../../components/as-modal/with-context';
import { bulkloadFulfillmentPlans } from '../../../../../../store/bulkload/bulkload.actions';
import { deallocateSuccess } from '../../../../../../store/inventory/inventory.actions';
import { getInventoryAllocationsForProduct } from '../../../../../../store/inventory/inventory.api';
import * as InventoryApi from '../../../../../../store/inventory/inventory.api';
import { ProductDetailsContext } from '../../../../context';
import { useProductDetailsContext } from '../../../../use-product-details-context';
import { messages } from './messages';
import style from '../style.module.less';

type InventoryAllocationItem = {
  fulfillmentPlanId: string;
  quantity: number;
  orderNumber: string;
  orderGridStatus: string;
  toteId: number;
};

const NoAllocationsMade = () => (
  <div className={style['no-allocations']}>
    <Text textType={TextType.Paragraph} value={messages.noAllocations} />
  </div>
);

export const AllocationsGridV3 = () => {
  const dispatch = useDispatch();
  const { productId } = useProductDetailsContext();
  const [inventoryAllocations, setInventoryAllocations] = useAsyncSafeState([] as InventoryAllocationItem[]);
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [isWarningModalOpen, setIsWarningModalOpen] = useAsyncSafeState(false);
  const [shouldLoadAllocations, setShouldLoadAllocations] = useAsyncSafeState(true);

  const onSetWarningOpen = useCallback(() => {
    setIsWarningModalOpen(!isWarningModalOpen);
  }, [isWarningModalOpen, setIsWarningModalOpen]);

  const onConfirmDeallocation = useCallback(() => {
    const fulfillmentPlanIds = inventoryAllocations.map((alloc: InventoryAllocationItem) => alloc.fulfillmentPlanId);
    InventoryApi.deallocateFulfillmentPlansPromise(fulfillmentPlanIds).then((data) => {
      const didSucceed = data.status === 'DeallocationSuccessful';
      if (!didSucceed) {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.deallocationFailureTitle} />,
          content: <FormattedMessage {...messages.deallocationFailureMessage} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
      } else {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.deallocationSuccessTitle} />,
          content: <FormattedMessage {...messages.deallocationSuccessMessage} />,
          notificationType: NOTIFICATION_TYPE_SUCCESS,
        });
      }
      setShouldLoadAllocations(true);
      dispatch(deallocateSuccess());
    });
    setIsWarningModalOpen(false);
  }, [dispatch, setIsWarningModalOpen, setShouldLoadAllocations, inventoryAllocations]);

  useEffect(() => {
    if (!productId) {
      setIsLoading(false);
    }
    const allocationsPromise = productId && shouldLoadAllocations ? getInventoryAllocationsForProduct(productId) : undefined;
    if (allocationsPromise) {
      allocationsPromise.promise.then((response) => {
        const allocations: InventoryAllocationItem[] = get(response, 'data.allocations', []);
        const fulfillmentPlanIds = allocations.map((alloc) => alloc.fulfillmentPlanId);
        dispatch(bulkloadFulfillmentPlans(fulfillmentPlanIds));
        setInventoryAllocations(allocations);
        setIsLoading(false);
        setShouldLoadAllocations(false);
      });
    }
    return () => allocationsPromise?.cancel();
  }, [productId, setInventoryAllocations, setIsLoading, dispatch, shouldLoadAllocations, setShouldLoadAllocations]);

  return (
    <>
      <FlexRow className={style['locations-container-flex']} gap={Gap.Space8}>
        <Text className={style['allocations-title']} element='div' textType={TextType.H4} value={messages.allocatedOrdersHeader} />
        <Button
          className={style['deallocation-button']}
          disabled={inventoryAllocations.length === 0}
          onClick={onSetWarningOpen}
          theme='secondary'
        >
          <FontAwesomeIcon icon={faMinus} /> <Text element='span' textType={TextType.Paragraph} value={messages.deallocateButton} />
        </Button>
      </FlexRow>
      <div className={style['allocations-container']}>
        <AllocationsHeader />
        {inventoryAllocations.map((inv: InventoryAllocationItem) => (
          <InventoryAllocation
            key={inv.orderNumber}
            fulfillmentPlanId={inv.fulfillmentPlanId}
            numberAllocated={inv.quantity}
            orderNumber={inv.orderNumber}
            orderStatus={inv.orderGridStatus}
            toteId={inv.toteId}
          />
        ))}
        {inventoryAllocations.length === 0 && !isLoading && <NoAllocationsMade />}
        {isLoading && <LoadingIndicator className={style['loading-indicator']} theme='grey' />}
        {isWarningModalOpen && (
          <AsModalWithContext Context={ProductDetailsContext}>
            <DeallocationWarningModal
              onCloseModal={onSetWarningOpen}
              onConfirmation={onConfirmDeallocation}
              orderCount={inventoryAllocations.length}
            />
          </AsModalWithContext>
        )}
      </div>
    </>
  );
};
